import { getDefaultScopeData } from '../store'
import type { ScopeData, Workflow } from '../types'

export const getDemoScopeData = (): ScopeData => {
  const scope = getDefaultScopeData()

  const flow1Id = 'flow_demo_1'
  const flow2Id = 'flow_demo_2'

  const workflow1: Workflow = {
    kind: 'builder',
    id: 'workflow_demo_1',
    name: 'Standard Individual Onboarding',
    notes: 'Default workflow for AU individuals. Includes ID verification and AML.',
    associatedFlowId: flow1Id,
    steps: [
      { id: 'step_demo_blocklist', type: 'blocklist' },
      { id: 'step_demo_govid', type: 'gov_id' },
      {
        id: 'step_demo_kyc',
        type: 'kyc',
        rulesetId: '2x2_gov_id',
        cascadeSources: ['dvs', 'equifax_au', 'australian_electoral_roll'],
      },
      { id: 'step_demo_idv', type: 'idv_biometrics', flowId: flow1Id, requirement: 'mandatory' },
      { id: 'step_demo_aml', type: 'aml' },
    ],
  }

  const workflow2: Workflow = {
    kind: 'builder',
    id: 'workflow_demo_2',
    name: 'Step-Up (High Risk)',
    notes: 'Adds risk branching and can step-up to ID verification for higher risk scenarios.',
    associatedFlowId: flow2Id,
    steps: [
      { id: 'step_demo2_blocklist', type: 'blocklist' },
      {
        id: 'step_demo2_kyc',
        type: 'kyc',
        rulesetId: '2x2_gov_id',
        cascadeSources: ['dvs', 'equifax_au'],
      },
      {
        id: 'step_demo2_risk',
        type: 'risk',
        branches: [
          {
            id: 'branch_demo2_1',
            condition: 'If risk is High or Unacceptable',
            outcome: '',
            steps: [
              { id: 'step_demo2_idv', type: 'idv_biometrics', flowId: flow2Id, requirement: 'optional' },
              { id: 'step_demo2_aml', type: 'aml' },
            ],
          },
          {
            id: 'branch_demo2_2',
            condition: 'If risk is Medium or Low',
            outcome: '',
            steps: [{ id: 'step_demo2_aml2', type: 'aml' }],
          },
        ],
      },
    ],
  }

  return {
    ...scope,
    customerName: 'Acme Bank',
    customerAbn: '12 345 678 901',
    generalInfo: {
      ...scope.generalInfo,
      parentChild: 'parent_only',
      productionAccessDate: '2026-04-01',
      goLiveDate: '2026-05-15',
      integrationType: 'both',
      ssoAccess: 'yes',
      aiFeatures: 'no',
      resellerAgreement: 'no',
      clientUseCase:
        'Customer onboarding for AU consumers. The client will use hosted onboarding links for ID capture and integrate results into their core banking platform.',
      phases: [
        'UAT configuration and validation with mock connectors.',
        'Production rollout for AU customers and monitoring.',
      ],
      testDetails:
        'UAT should use mock connectors (no live connectors). Please provide a test plan covering 2+2, failure cases, and step-up scenarios. We will run joint UAT sessions with Compliance and Engineering.',
    },
    kycConfig: {
      ...scope.kycConfig,
      isReportingEntity: 'yes',
      vendor: 'frankieone',
      complianceFramework: 'safe_harbour_2x2',
      minimumDataSources: '2',
      enableCascade: 'yes',
      requiredDataElements: {
        ...scope.kycConfig.requiredDataElements,
        fullName: 'yes',
        dateOfBirth: 'yes',
        address: 'yes',
        governmentId: 'yes',
      },
      auGovernmentSources: {
        dvsDirect: 'yes',
        dvsViaEquifax: 'no',
        vevo: 'no',
        electoralRoll: 'yes',
      },
      creditBureauSources: {
        equifaxCreditHeader: 'yes',
        equifaxCreditHeaderVia: 'frankieone',
        equifaxCreditHeaderDirectNote: '',
        experianCreditHeader: 'no',
        experianCreditHeaderVia: 'unknown',
        experianCreditHeaderDirectNote: '',
      },
      primaryCustomerJurisdictions: ['Australia'],
      internationalCountries: ['None'],
      documentTypesAu: ['passport', 'drivers_license', 'medicare'],
      acceptNzCustomers: 'no',
    },
    amlConfig: {
      ...scope.amlConfig,
      level: 'standard',
      relationship: 'frankieone',
      vendor: 'comply_advantage',
      pepLevels: ['pep', 'rca'],
      monitoringType: 'one-time',
      screeningEntityTypes: {
        individuals: 'yes',
        businesses: 'no',
        ubos: 'unknown',
        directorsAndOfficers: 'unknown',
        authorizedRepresentatives: 'unknown',
      },
      ongoingMonitoringEnabled: 'no',
    },
    biometricsConfig: {
      ...scope.biometricsConfig,
      enabled: 'yes',
      serviceLevel: 'document_biometrics_review',
      frontend: 'hosted_onesdk',
      platform: 'web',
      vendor: 'frankieone',
      smsLinkValidityHours: 24,
      customSmsMessageTemplate:
        'Hi {{customer_name}}, please complete your identity verification to open your account. It takes about 2 minutes.',
      flows: [
        {
          id: flow1Id,
          name: 'Standard (HostedOneSDK)',
          notes: 'Use 2 ID capture setting for Hosted OneSDK.',
          isDefault: true,
          idVerificationEnabled: 'yes',
          idFormEnabled: 'yes',
          biometricsEnabled: 'yes',
          reviewScreenEnabled: 'yes',
          consentScreenEnabled: 'yes',
          redirectPageEnabled: 'yes',
          customScreenEnabled: 'unknown',
          customScreenDetails: '',
          vendor: 'daon',
          documentTypes: ['passport', 'drivers_license', 'medicare_manual'],
          customDocumentTypeDetails: '',
          doubleIdEnabled: 'unknown',
        },
        {
          id: flow2Id,
          name: 'Step-Up (High Risk)',
          notes: 'Step-up flow used when the customer requires additional checks.',
          isDefault: false,
          idVerificationEnabled: 'yes',
          idFormEnabled: 'no',
          biometricsEnabled: 'yes',
          reviewScreenEnabled: 'yes',
          consentScreenEnabled: 'yes',
          redirectPageEnabled: 'yes',
          customScreenEnabled: 'unknown',
          customScreenDetails: '',
          vendor: 'incode',
          documentTypes: ['passport', 'drivers_license'],
          customDocumentTypeDetails: '',
          doubleIdEnabled: 'unknown',
        },
      ],
    },
    workflowsConfig: [workflow1, workflow2],
    rulesConfig: {
      ...scope.rulesConfig,
      duplicateRules: 'default',
      blocklistRules: 'custom',
      customBlocklistRules:
        'Blocklist matching should require a strong match on ID Number + DOB, and moderate match on name + DOB. Final thresholds to be confirmed with Compliance.',
    },
    finalized: false,
  }
}
